import { webLink } from "@/lib/web-link"
import { useState } from "react"
import type { ReactNode } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useApiMutation } from "@/hooks/use-api-mutation"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import {
  validateJuryToken,
  getJuryCandidates,
  getPublicGradeStatuses,
  submitJuryGrade,
  type JuryCandidate,
  type SubmitGradeRequest,
} from "@/services/training"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useToast } from "@/hooks/use-toast"
import { LoadErrorAlert } from "@/components/LoadErrorAlert"
import { TenantClosedNotice } from "@/components/TenantClosedNotice"
import { isTenantClosedError } from "@/lib/tenant-closed"
import { extractErrorStatus, translateError } from "@/lib/error-messages"
import {
  Award,
  Calendar,
  MapPin,
  User,
  Users,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
} from "lucide-react"
import { formatIsoDateFr } from "@/lib/organisation-day"

type GradeSubmission = { registrationId: string; grade: SubmitGradeRequest }

// Carte des candidats selon l'état de la requête.
function CandidatesState({
  status,
  count,
  errorAlert,
  children,
}: {
  status: "pending" | "error" | "success"
  count: number
  errorAlert: () => ReactNode
  children: ReactNode
}) {
  if (status === "pending") {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin motion-reduce:animate-none" />
      </div>
    )
  }
  if (status === "error") return errorAlert()
  if (count === 0) {
    return (
      <div className="text-center py-12">
        <Users className="size-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Aucun candidat à évaluer</p>
      </div>
    )
  }
  return children
}

// Seuls 401 et 404 disent un lien invalide ou expiré.
// Toute autre erreur se réessaie.
function isInvalidLink(error: unknown): boolean {
  const status = extractErrorStatus(error)
  return status === 401 || status === 404
}

export function JuryPortalPage() {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()

  const [selectedCandidate, setSelectedCandidate] = useState<JuryCandidate | null>(null)
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false)
  const [gradeForm, setGradeForm] = useState<SubmitGradeRequest>({
    grade_status_code: "",
    comment: "",
    private_note: "",
  })

  // Validate token and get session info
  // comply-ignore: tanstack-query-prefer-suspense-query — un échec doit rester un LoadErrorAlert avec Réessayer (spec L3.3), pas la page de RouteErrorBoundary.
  const {
    data: session,
    isLoading: isLoadingSession,
    error: sessionError,
    isError: isSessionError,
    refetch: refetchSession,
    isFetching: isFetchingSession,
  } = useQuery({
    queryKey: ["jury-portal-session", token],
    queryFn: () => validateJuryToken(token!),
    enabled: !!token,
    retry: false,
  })

  // Get candidates list
  // comply-ignore: tanstack-query-prefer-suspense-query — un échec doit rester un LoadErrorAlert avec Réessayer (spec L3.3), pas la page de RouteErrorBoundary.
  const {
    data: candidates = [],
    status: candidatesStatus,
    error: candidatesError,
    refetch: refetchCandidates,
    isFetching: isFetchingCandidates,
  } = useQuery({
    queryKey: ["jury-candidates", token],
    queryFn: () => getJuryCandidates(token!),
    enabled: !!token && !!session,
  })

  // Get grade statuses
  // comply-ignore: tanstack-query-prefer-suspense-query — un échec doit rester un LoadErrorAlert avec Réessayer (spec L3.3), pas la page de RouteErrorBoundary.
  const {
    data: gradeStatuses = [],
    isError: isGradeStatusesError,
    error: gradeStatusesError,
    refetch: refetchGradeStatuses,
    isFetching: isFetchingGradeStatuses,
  } = useQuery({
    queryKey: ["jury-grade-statuses", token],
    queryFn: () => getPublicGradeStatuses(token!),
    enabled: !!token && !!session,
  })

  const portalToken = pipe(
    O.fromNullable(token),
    O.getOrElse(() => ""),
  )
  // Un refus du serveur s'affiche traduit, par son code.
  const submitGradeMutation = useApiMutation(
    "jury-candidates",
    (submission: GradeSubmission) =>
      submitJuryGrade(portalToken, submission.registrationId, submission.grade),
    {
      errorTitle: "Impossible d'enregistrer la note",
      onSuccess: () => {
        toast({ title: "Note enregistrée", description: "La note a été enregistrée avec succès." })
        setGradeDialogOpen(false)
        setSelectedCandidate(null)
        setGradeForm({ grade_status_code: "", comment: "", private_note: "" })
      },
    },
  )

  // Loading state
  if (isLoadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground" data-test="jury-access-loading">Vérification de votre accès...</p>
        </div>
      </div>
    )
  }

  const retrySession = () => {
    void refetchSession()
  }
  const retryCandidates = () => {
    void refetchCandidates()
  }
  const retryGradeStatuses = () => {
    void refetchGradeStatuses()
  }

  // Organisme fermé (410 TENANT_CLOSED) : refus définitif, rien à réessayer.
  if (isSessionError && isTenantClosedError(sessionError)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <TenantClosedNotice />
      </div>
    )
  }

  // Panne du serveur : l'erreur se réessaie, le lien reste bon.
  if (isSessionError && !isInvalidLink(sessionError)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full">
          <LoadErrorAlert
            message={translateError(sessionError)}
            onRetry={retrySession}
            isRetrying={isFetchingSession}
            dataTest="jury-portal-load-error"
          />
        </div>
      </div>
    )
  }

  // Error state
  if (sessionError || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full" data-test="jury-access-denied">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Accès refusé</CardTitle>
            <CardDescription>
              Le lien d'accès est invalide ou a expiré. Veuillez contacter l'organisateur pour
              obtenir un nouveau lien.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  function handleGradeCandidate(candidate: JuryCandidate) {
    setSelectedCandidate(candidate)
    setGradeForm({
      grade_status_code: candidate.my_grade?.grade_status_code || "",
      comment: candidate.my_grade?.comment || "",
      private_note: "",
    })
    setGradeDialogOpen(true)
  }

  function handleSubmitGrade() {
    if (!selectedCandidate || !gradeForm.grade_status_code) return

    // Check if comment is required
    const selectedStatus = gradeStatuses.find((s) => s.code === gradeForm.grade_status_code)
    if (selectedStatus?.requires_comment && !gradeForm.comment) {
      toast({
        title: "Commentaire requis",
        description: "Un commentaire est requis pour ce statut de note.",
        variant: "destructive",
      })
      return
    }

    submitGradeMutation.mutate({
      registrationId: selectedCandidate.registration_id,
      grade: gradeForm,
    })
  }

  // Construite seulement en cas d'échec, quand l'erreur existe.
  const candidatesErrorAlert = () => (
    <LoadErrorAlert
      message={translateError(candidatesError)}
      onRetry={retryCandidates}
      isRetrying={isFetchingCandidates}
      dataTest="jury-candidates-load-error"
    />
  )

  const gradedCount = candidates.filter((c) => c.has_been_graded).length
  const totalCount = candidates.length
  const progress = totalCount > 0 ? Math.round((gradedCount / totalCount) * 100) : 0

  return (
    <div className="min-h-screen bg-background" data-test="jury-portal-page">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-xl font-bold">Portail Jury</h1>
                <p className="text-sm text-muted-foreground">{session.program_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant={session.is_president ? "default" : "secondary"} data-test="jury-portal-member-role">
                {session.member_role_label}
              </Badge>
              <span className="text-sm font-medium">{session.member_name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-8">
        {/* Session Info */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardDescription>Date du jury</CardDescription>
                <CardTitle className="text-lg">
                  {formatIsoDateFr(session.scheduled_date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </CardTitle>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <MapPin className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardDescription>Lieu</CardDescription>
                <CardTitle className="text-lg" data-test="jury-portal-venue">
                  {[session.venue_name, session.room].find(Boolean) ?? "À distance"}
                </CardTitle>
              </div>
            </CardHeader>
            {session.remote_url && (
              <CardContent className="pt-0">
                {pipe(
                  webLink(session.remote_url),
                  O.match(
                    () => (
                      <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]" data-test="jury-portal-remote-text">
                        {session.remote_url}
                      </span>
                    ),
                    (href) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline focus:ring-2 focus:ring-primary flex items-center gap-1"
                      >
                        <ExternalLink className="size-3" />
                        Rejoindre la visio
                      </a>
                    ),
                  ),
                )}
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardDescription>Progression</CardDescription>
                <CardTitle className="text-lg">
                  {gradedCount} / {totalCount} candidats notés
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Candidates List */}
        <Card>
          <CardHeader>
            <CardTitle>Candidats à évaluer</CardTitle>
            <CardDescription>
              Cliquez sur un candidat pour saisir ou modifier sa note
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CandidatesState
              status={candidatesStatus}
              count={candidates.length}
              errorAlert={candidatesErrorAlert}
            >
              <Table data-test="candidates-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidat</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Ma note</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.map((candidate) => (
                    <TableRow key={candidate.registration_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {candidate.contact_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {candidate.has_been_graded ? (
                          <Badge variant="default" className="bg-green-500">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Noté
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            À évaluer
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {candidate.my_grade ? (
                          <Badge
                            data-test="grade-badge"
                            style={{
                              backgroundColor: gradeStatuses.find(
                                (s) => s.code === candidate.my_grade?.grade_status_code
                              )?.color,
                            }}
                          >
                            {candidate.my_grade.grade_status_label}
                          </Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant={candidate.has_been_graded ? "outline" : "default"}
                          onClick={() => handleGradeCandidate(candidate)}
                          data-test="grade-candidate-btn"
                        >
                          {candidate.has_been_graded ? "Modifier" : "Noter"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CandidatesState>
          </CardContent>
        </Card>
      </main>

      {/* Grade Dialog */}
      <Dialog open={gradeDialogOpen} onOpenChange={setGradeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Évaluer le candidat</DialogTitle>
            <DialogDescription>
              {selectedCandidate?.contact_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Note *</Label>
              {isGradeStatusesError && (
                <LoadErrorAlert
                  message={translateError(gradeStatusesError)}
                  onRetry={retryGradeStatuses}
                  isRetrying={isFetchingGradeStatuses}
                  dataTest="jury-grade-statuses-load-error"
                />
              )}
              <RadioGroup
                value={gradeForm.grade_status_code}
                onValueChange={(value) => setGradeForm({ ...gradeForm, grade_status_code: value })}
                className="space-y-2"
                data-test="grade-status-radio"
              >
                {gradeStatuses.map((status) => (
                  <div
                    key={status.code}
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => setGradeForm({ ...gradeForm, grade_status_code: status.code })}
                  >
                    <RadioGroupItem
                      value={status.code}
                      id={status.code}
                      data-test={`grade-status-option-${status.code}`}
                    />
                    <Label
                      htmlFor={status.code}
                      className="flex items-center gap-2 cursor-pointer flex-1"
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: status.color }}
                      />
                      <span>{status.label}</span>
                      {status.requires_comment && (
                        <span className="text-xs text-muted-foreground">(commentaire requis)</span>
                      )}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment">
                Commentaire
                {gradeStatuses.find((s) => s.code === gradeForm.grade_status_code)
                  ?.requires_comment && " *"}
              </Label>
              <Textarea
                id="comment"
                placeholder="Observations sur le candidat..."
                value={gradeForm.comment || ""}
                onChange={(e) => setGradeForm({ ...gradeForm, comment: e.target.value })}
                rows={3}
                data-test="grade-comment"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="private_note">Note privée (visible uniquement par le président)</Label>
              <Textarea
                id="private_note"
                placeholder="Note confidentielle..."
                value={gradeForm.private_note || ""}
                onChange={(e) => setGradeForm({ ...gradeForm, private_note: e.target.value })}
                rows={2}
                data-test="grade-private-note"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGradeDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmitGrade}
              disabled={!gradeForm.grade_status_code || submitGradeMutation.isPending}
              data-test="submit-grade-btn"
            >
              {submitGradeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
